import { useEffect, useState, useMemo } from 'react';
import { AdminSidebar } from '../../components/admin/AdminSidebar';
import {
  adminListUsers,
  grantAccess,
  revokeGrant,
  type AdminUserRecord,
} from '@shared/services/admin-api';
import { Search, ChevronUp, ChevronDown, Gift, XCircle } from 'lucide-react';
import { Button } from '../../components/ui/Button';

type SortKey = 'email' | 'tier' | 'createdAt' | 'lastActiveAt';
type SortDir = 'asc' | 'desc';

export function AdminUsers() {
  const [users, setUsers] = useState<AdminUserRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [tierFilter, setTierFilter] = useState<string>('all');
  const [sortKey, setSortKey] = useState<SortKey>('createdAt');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  // Grant modal
  const [grantTarget, setGrantTarget] = useState<AdminUserRecord | null>(null);
  const [grantTier, setGrantTier] = useState<'learner' | 'pro' | 'teams'>('pro');
  const [grantCoach, setGrantCoach] = useState(true);
  const [grantDuration, setGrantDuration] = useState('');
  const [grantNote, setGrantNote] = useState('');
  const [grantCreateTeam, setGrantCreateTeam] = useState(true);
  const [grantTeamName, setGrantTeamName] = useState('');
  const [grantTeamSeats, setGrantTeamSeats] = useState('5');
  const [grantCoachSeats, setGrantCoachSeats] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    loadUsers();
  }, []);

  async function loadUsers() {
    setLoading(true);
    try {
      const { users } = await adminListUsers();
      setUsers(users);
    } catch (err) {
      console.error('Failed to load users:', err);
    } finally {
      setLoading(false);
    }
  }

  function toggleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  }

  const filtered = useMemo(() => {
    let result = users;
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(
        (u) =>
          u.email?.toLowerCase().includes(q) ||
          u.displayName?.toLowerCase().includes(q) ||
          u.uid.toLowerCase().includes(q),
      );
    }
    if (tierFilter !== 'all') {
      result = result.filter((u) => u.tier === tierFilter);
    }
    result = [...result].sort((a, b) => {
      let aVal: string | number = '';
      let bVal: string | number = '';
      if (sortKey === 'email') {
        aVal = a.email ?? '';
        bVal = b.email ?? '';
      } else if (sortKey === 'tier') {
        aVal = a.tier;
        bVal = b.tier;
      } else if (sortKey === 'createdAt') {
        aVal = a.createdAt ?? 0;
        bVal = b.createdAt ?? 0;
      } else if (sortKey === 'lastActiveAt') {
        aVal = a.lastActiveAt ?? 0;
        bVal = b.lastActiveAt ?? 0;
      }
      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return result;
  }, [users, search, tierFilter, sortKey, sortDir]);

  async function onGrant() {
    if (!grantTarget) return;
    setBusy(true);
    setMessage(null);
    try {
      const parsedDuration = grantDuration.trim() === '' ? null : Number(grantDuration);
      if (parsedDuration !== null && (!Number.isInteger(parsedDuration) || parsedDuration <= 0)) {
        throw new Error('Duration must be a positive integer or blank');
      }
      const wantTeam = grantTier === 'teams' && grantCreateTeam;
      const parsedSeats = grantCoachSeats.trim() === '' ? null : Number(grantCoachSeats);
      if (parsedSeats !== null && (!Number.isInteger(parsedSeats) || parsedSeats < 0)) {
        throw new Error('Coach seats must be a non-negative integer or blank');
      }
      const parsedTeamSeats = grantTeamSeats.trim() === '' ? null : Number(grantTeamSeats);
      if (wantTeam && parsedTeamSeats !== null && (!Number.isInteger(parsedTeamSeats) || parsedTeamSeats < 1)) {
        throw new Error('Team seats must be a positive integer (includes the founder)');
      }
      const result = await grantAccess({
        userId: grantTarget.uid,
        tier: grantTier,
        coachAddOn: grantCoach,
        durationDays: parsedDuration,
        note: grantNote.trim() || null,
        createTeamForUser: wantTeam,
        teamName: wantTeam ? (grantTeamName.trim() || null) : null,
        teamSeats: wantTeam ? parsedTeamSeats : null,
        coachSeats: wantTeam ? parsedSeats : null,
      });
      setMessage(
        `Granted ${grantTier}${grantCoach ? ' + Coach' : ''} to ${grantTarget.email ?? grantTarget.uid}` +
          (result.teamId ? ` · team created` : ''),
      );
      setGrantTarget(null);
      setGrantDuration('');
      setGrantNote('');
      setGrantTeamName('');
      setGrantTeamSeats('5');
      setGrantCoachSeats('');
      await loadUsers();
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Grant failed');
    } finally {
      setBusy(false);
    }
  }

  async function onRevoke(user: AdminUserRecord) {
    setBusy(true);
    setMessage(null);
    try {
      await revokeGrant(user.uid);
      setMessage(`Revoked grant for ${user.email ?? user.uid}`);
      await loadUsers();
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Revoke failed');
    } finally {
      setBusy(false);
    }
  }

  const SortIcon = ({ k }: { k: SortKey }) => {
    if (sortKey !== k) return null;
    return sortDir === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />;
  };

  const tierColors: Record<string, string> = {
    free: '#6B7280',
    learner: '#6C5CE7',
    pro: '#F0A500',
    teams: '#0ABFBC',
  };

  return (
    <div className="min-h-screen bg-dark-bg">
      <div className="flex">
        <AdminSidebar />
        <main className="flex-1 px-4 py-6 sm:px-6 lg:px-8 lg:py-8">
          <h1 className="font-heading text-2xl font-bold mb-1">User Management</h1>
          <p className="text-content-muted text-sm mb-6">
            {users.length} total users · Search, filter, grant access
          </p>

          {message && (
            <div
              className="rounded-lg text-sm p-3 mb-4"
              style={{
                background: 'rgba(108,92,231,0.1)',
                border: '1px solid rgba(108,92,231,0.25)',
                color: '#A39BE0',
              }}
            >
              {message}
            </div>
          )}

          {/* Search & filter bar */}
          <div className="flex flex-col sm:flex-row gap-3 mb-5">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-content-muted" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by email, name, or UID…"
                className="w-full rounded-lg text-sm pl-9 pr-3 py-2.5 bg-dark-surface border border-dark-border text-content focus:border-indigo-500 focus:outline-none"
              />
            </div>
            <select
              value={tierFilter}
              onChange={(e) => setTierFilter(e.target.value)}
              className="rounded-lg text-sm px-3 py-2.5 bg-dark-surface border border-dark-border text-content"
            >
              <option value="all">All tiers</option>
              <option value="free">Free</option>
              <option value="learner">Learner</option>
              <option value="pro">Pro</option>
              <option value="teams">Teams</option>
            </select>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <div className="rounded-2xl bg-dark-surface border border-dark-border overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-dark-border">
                      <Th onClick={() => toggleSort('email')}>User <SortIcon k="email" /></Th>
                      <Th onClick={() => toggleSort('tier')}>Tier <SortIcon k="tier" /></Th>
                      <th className="px-4 py-3 text-left text-[11px] uppercase tracking-wider text-content-muted font-semibold">Grant</th>
                      <Th onClick={() => toggleSort('createdAt')}>Joined <SortIcon k="createdAt" /></Th>
                      <Th onClick={() => toggleSort('lastActiveAt')}>Last Active <SortIcon k="lastActiveAt" /></Th>
                      <th className="px-4 py-3 text-right text-[11px] uppercase tracking-wider text-content-muted font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-4 py-10 text-center text-content-muted">
                          No users match your search.
                        </td>
                      </tr>
                    ) : (
                      filtered.map((u) => (
                        <tr key={u.uid} className="border-b border-dark-border/50 hover:bg-white/[0.02] transition-colors">
                          <td className="px-4 py-3">
                            <div className="text-content font-medium truncate max-w-[220px]">
                              {u.displayName ?? u.email ?? u.uid}
                            </div>
                            <div className="text-[11px] text-content-muted truncate max-w-[220px]">
                              {u.email ?? u.uid}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span
                              className="inline-block text-[11px] font-semibold rounded-md px-2 py-0.5"
                              style={{
                                color: tierColors[u.tier] ?? '#6B7280',
                                background: `${tierColors[u.tier] ?? '#6B7280'}15`,
                              }}
                            >
                              {u.tier}{u.coachAddOn ? ' + Coach' : ''}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            {u.hasGrant ? (
                              <span className="text-[11px] text-green-400">
                                Active{u.grantExpiresAt ? ` · ${new Date(u.grantExpiresAt).toLocaleDateString()}` : ' · ∞'}
                              </span>
                            ) : (
                              <span className="text-[11px] text-content-dim">—</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-[12px] text-content-muted font-code">
                            {u.createdAt ? new Date(u.createdAt).toLocaleDateString() : '—'}
                          </td>
                          <td className="px-4 py-3 text-[12px] text-content-muted font-code">
                            {u.lastActiveAt ? new Date(u.lastActiveAt).toLocaleDateString() : '—'}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                onClick={() => {
                                  setGrantTarget(u);
                                  setMessage(null);
                                }}
                                className="text-[11px] text-indigo-400 hover:text-indigo-300 flex items-center gap-1"
                                title="Grant access"
                              >
                                <Gift size={13} /> Grant
                              </button>
                              {u.hasGrant && (
                                <button
                                  onClick={() => onRevoke(u)}
                                  disabled={busy}
                                  className="text-[11px] text-red-400 hover:text-red-300 flex items-center gap-1 disabled:opacity-40"
                                  title="Revoke grant"
                                >
                                  <XCircle size={13} /> Revoke
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              <div className="px-4 py-3 text-[11px] text-content-muted border-t border-dark-border">
                Showing {filtered.length} of {users.length} users
              </div>
            </div>
          )}

          {/* Grant modal */}
          {grantTarget && (
            <>
              <div className="fixed inset-0 z-50 bg-black/60" onClick={() => setGrantTarget(null)} />
              <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                <div
                  className="w-full max-w-md rounded-2xl p-6"
                  style={{
                    background: 'linear-gradient(180deg, #141830, #0E1225)',
                    border: '1px solid #2A2F4F',
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <h3 className="font-heading text-lg font-bold mb-1">Grant Access</h3>
                  <p className="text-sm text-content-muted mb-4">
                    {grantTarget.displayName ?? grantTarget.email ?? grantTarget.uid}
                  </p>

                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-[11px] uppercase tracking-wider text-content-muted mb-1">Tier</label>
                      <select
                        value={grantTier}
                        onChange={(e) => setGrantTier(e.target.value as 'learner' | 'pro' | 'teams')}
                        className="w-full rounded-lg text-sm p-2.5 bg-dark-subtle border border-dark-border text-content"
                      >
                        <option value="learner">Learner</option>
                        <option value="pro">Pro</option>
                        <option value="teams">Teams</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-[11px] uppercase tracking-wider text-content-muted mb-1">Duration (days)</label>
                      <input
                        value={grantDuration}
                        onChange={(e) => setGrantDuration(e.target.value)}
                        placeholder="∞"
                        className="w-full rounded-lg text-sm p-2.5 bg-dark-subtle border border-dark-border text-content"
                      />
                    </div>
                  </div>

                  <label className="flex items-center gap-2 text-sm mb-4">
                    <input
                      type="checkbox"
                      checked={grantCoach}
                      onChange={(e) => setGrantCoach(e.target.checked)}
                    />
                    Include Coach add-on
                  </label>

                  {grantTier === 'teams' && (
                    <div className="rounded-lg border border-dark-border bg-dark-subtle/50 p-3 mb-4">
                      <label className="flex items-center gap-2 text-sm mb-3">
                        <input
                          type="checkbox"
                          checked={grantCreateTeam}
                          onChange={(e) => setGrantCreateTeam(e.target.checked)}
                        />
                        Create a team for this user (team admin + roster)
                      </label>
                      {grantCreateTeam && (
                        <>
                          <label className="block text-[11px] uppercase tracking-wider text-content-muted mb-1">Team name (optional)</label>
                          <input
                            value={grantTeamName}
                            onChange={(e) => setGrantTeamName(e.target.value)}
                            placeholder="e.g. Acme Cohort"
                            className="w-full rounded-lg text-sm p-2.5 mb-3 bg-dark-subtle border border-dark-border text-content"
                          />
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-[11px] uppercase tracking-wider text-content-muted mb-1">Team seats (incl. founder)</label>
                              <input
                                value={grantTeamSeats}
                                onChange={(e) => setGrantTeamSeats(e.target.value)}
                                placeholder="5"
                                className="w-full rounded-lg text-sm p-2.5 bg-dark-subtle border border-dark-border text-content"
                              />
                            </div>
                            <div>
                              <label className="block text-[11px] uppercase tracking-wider text-content-muted mb-1">Coach seats (optional)</label>
                              <input
                                value={grantCoachSeats}
                                onChange={(e) => setGrantCoachSeats(e.target.value)}
                                placeholder="0"
                                className="w-full rounded-lg text-sm p-2.5 bg-dark-subtle border border-dark-border text-content"
                              />
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  <label className="block text-[11px] uppercase tracking-wider text-content-muted mb-1">Note</label>
                  <input
                    value={grantNote}
                    onChange={(e) => setGrantNote(e.target.value)}
                    placeholder="e.g. beta tester, reviewer"
                    className="w-full rounded-lg text-sm p-2.5 mb-5 bg-dark-subtle border border-dark-border text-content"
                  />

                  <div className="flex items-center justify-end gap-3">
                    <button
                      onClick={() => setGrantTarget(null)}
                      className="text-sm text-content-muted hover:text-content"
                    >
                      Cancel
                    </button>
                    <Button onClick={onGrant} disabled={busy}>
                      {busy ? 'Granting…' : 'Grant access'}
                    </Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </main>
      </div>
    </div>
  );
}

function Th({ children, onClick }: { children: React.ReactNode; onClick?: () => void }) {
  return (
    <th
      onClick={onClick}
      className={`px-4 py-3 text-left text-[11px] uppercase tracking-wider text-content-muted font-semibold ${
        onClick ? 'cursor-pointer hover:text-content select-none' : ''
      }`}
    >
      <span className="inline-flex items-center gap-1">{children}</span>
    </th>
  );
}
